import _ from "lodash";
/**--external-- */
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

/**--internal-- */
import { useI18n } from "Hooks";
import { getStyleStrippedText } from "Utils";
import { colors } from "Constants";
import { getHTMLLabel } from "GenericTableEditor/GenericTableEditorUtils";
import { SINGLE_LEVEL } from "Fields/FieldUtils";

/**--relative-- */
import classes from "./LabelCell.scss";

const REMOVE_ERROR_TIMEOUT = 7000;

const MultiLevelLabelCell = props => {
  const {
    placeholderText,
    handleOnInput,
    handleContentEdit,
    label,
    handleOnPaste,
    depth,
    searchText,
    isEditable,
  } = props;

  const { t } = useI18n(["common"]);

  const textContainerClasses = classNames(classes.textContainer, {
    ["heading-5"]: depth == 1,
    ["heading-6"]: depth == 2,
    ["text-label-3"]: depth == 3,
    ["text-body-3"]: depth == 4,
    [`text-body-3 ${classes.depth5}`]: depth == 5,
  });

  const textContainerStyle = {
    marginLeft: `${(depth - 1) * 30}px`,
  };

  const htmlLabel = getHTMLLabel({
    label,
    searchText,
    style: {
      color: colors.blue400,
    },
  });

  return (
    <div className={classes.container}>
      <div
        className={textContainerClasses}
        contentEditable={isEditable}
        suppressContentEditableWarning
        placeholder={placeholderText}
        onInput={handleOnInput}
        onBlur={handleContentEdit}
        onPasteCapture={handleOnPaste}
        dangerouslySetInnerHTML={{ __html: htmlLabel }}
        style={textContainerStyle}
      />
    </div>
  );
};

const SingleLevelLabelCell = props => {
  const {
    placeholderText,
    handleOnInput,
    handleContentEdit,
    label,
    handleOnPaste,
    searchText,
    isEditable,
  } = props;

  const { t } = useI18n(["common"]);

  const htmlLabel = getHTMLLabel({
    label,
    searchText,
    style: {
      color: colors.blue400,
    },
  });

  const textContainerClasses = classNames(classes.textContainer, "text-body-1");

  return (
    <div className={classes.container}>
      <div
        className={textContainerClasses}
        contentEditable={isEditable}
        suppressContentEditableWarning
        placeholder={placeholderText}
        onInput={handleOnInput}
        onBlur={handleContentEdit}
        onPasteCapture={handleOnPaste}
        dangerouslySetInnerHTML={{ __html: htmlLabel }}
      />
    </div>
  );
};

const LabelCell = props => {
  const {
    label,
    placeholderText,
    cellName,
    onContentEdit,
    onBulkPasteRows,
    shouldConsiderNewRowOnPaste,
    removeError,
    isMandatory,
    isError,
    variant,
    depth,
    searchText,
    isEditable,
  } = props;

  const resolveError = _.debounce(
    () => {
      removeError();
    },
    REMOVE_ERROR_TIMEOUT,
    { leading: true }
  );

  const handleOnInput = event => {
    const updatedLabel = getStyleStrippedText({
      text: event.currentTarget.innerHTML,
      type: "SNS_EDITOR",
    });

    if (isMandatory && !_.isEmpty(updatedLabel) && isError) {
      resolveError();
    }
  };

  const handleContentEdit = event => {
    event.stopPropagation();
    const updatedLabel = getStyleStrippedText({
      text: event.currentTarget.innerHTML,
      type: "SNS_EDITOR",
    });
    const prevLabel = label;

    if (updatedLabel !== prevLabel || _.isEmpty(updatedLabel)) {
      const params = {
        [cellName]: updatedLabel,
      };
      onContentEdit({ params });
    }
  };

  const handleOnPaste = event => {
    const pastedData = event.clipboardData.getData("Text");
    const itemsList = pastedData.split(/\r?\n/); // regex spitting the excel sheet data into a list;
    const itemsListLength = _.size(itemsList);
    if (itemsListLength > 1) {
      // the list will be pasted in row format
      event.preventDefault();
      onBulkPasteRows({
        pastedData,
        cellName,
        isNewRow: shouldConsiderNewRowOnPaste,
        itemsList,
      });
    }
  };

  return variant === SINGLE_LEVEL ? (
    <SingleLevelLabelCell
      placeholderText={placeholderText}
      handleOnInput={handleOnInput}
      handleContentEdit={handleContentEdit}
      label={label}
      handleOnPaste={handleOnPaste}
      searchText={searchText}
      isEditable={isEditable}
    />
  ) : (
    <MultiLevelLabelCell
      placeholderText={placeholderText}
      handleOnInput={handleOnInput}
      handleContentEdit={handleContentEdit}
      label={label}
      handleOnPaste={handleOnPaste}
      depth={depth}
      searchText={searchText}
      isEditable={isEditable}
    />
  );
};

export default LabelCell;

LabelCell.defaultProps = {
  label: "",
  shouldConsiderNewRowOnPaste: false,
  variant: SINGLE_LEVEL,
  searchText: "",
  isEditable: true,
};

LabelCell.propTypes = {
  label: PropTypes.string,
  placeholderText: PropTypes.string.isRequired,
  cellName: PropTypes.string.isRequired,
  onContentEdit: PropTypes.func.isRequired,
  onBulkPasteRows: PropTypes.func.isRequired,
  shouldConsiderNewRowOnPaste: PropTypes.bool,
  isEditable: PropTypes.bool,
};
